import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import LanguageSelector from "@/components/LanguageSelector";
import {
  ArrowLeft,
  Sparkle,
  WhatsappLogo,
  ChatCircleDots,
  Clock,
  Bank,
} from "@phosphor-icons/react";

export default function SupportPage() {
  // Reuse the Support content already localised in the "terms" namespace.
  const { t } = useTranslation("terms");

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main
        data-testid="support-root"
        className="flex-1 mx-auto max-w-4xl px-5 md:px-8 py-10 md:py-16 w-full"
      >
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-200 inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
          <LanguageSelector />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            <Sparkle size={11} weight="fill" /> {t("support.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
            {t("support.title")}
          </h1>
          <p className="mt-4 text-neutral-400 text-base md:text-[17px] leading-relaxed max-w-2xl">
            {t("support.body")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10"
        >
          <a
            href="https://wa.me/917075521016"
            target="_blank"
            rel="noreferrer"
            className="lele-card p-6 flex flex-col justify-between hover:border-emerald-400/30 transition-colors group"
            data-testid="support-whatsapp-link"
          >
            <div>
              <span className="inline-flex h-9 w-9 rounded-lg bg-emerald-500/10 border border-emerald-400/30 items-center justify-center mb-3">
                <WhatsappLogo size={17} weight="fill" className="text-emerald-300" />
              </span>
              <div className="font-heading text-lg mb-1">
                {t("support.whatsappTitle")}
              </div>
              <div className="text-[13px] text-neutral-500">
                {t("support.whatsappHint")}
              </div>
            </div>
            <div className="mt-4 text-[13px] text-emerald-200 group-hover:text-emerald-100 inline-flex items-center gap-1.5">
              <WhatsappLogo size={13} weight="fill" />
              {t("support.whatsappOpen") || "Open WhatsApp"}
            </div>
          </a>

          <div className="lele-card p-6 flex flex-col justify-between" data-testid="support-chat-card">
            <div>
              <span className="inline-flex h-9 w-9 rounded-lg bg-indigo-500/10 border border-indigo-400/30 items-center justify-center mb-3">
                <ChatCircleDots size={17} weight="fill" className="text-indigo-300" />
              </span>
              <div className="font-heading text-lg mb-1">
                {t("support.chatTitle")}
              </div>
              <div className="text-[13px] text-neutral-500">
                {t("support.chatHint")}
              </div>
            </div>
            <div className="mt-4 text-[13px] text-indigo-200">
              {t("support.chatFooter")}
            </div>
          </div>

          <div className="lele-card p-6 flex flex-col justify-between" data-testid="support-hours-card">
            <div>
              <span className="inline-flex h-9 w-9 rounded-lg bg-violet-500/10 border border-violet-400/30 items-center justify-center mb-3">
                <Clock size={17} weight="fill" className="text-violet-300" />
              </span>
              <div className="font-heading text-lg mb-1">
                {t("support.hoursTitle")}
              </div>
              <div className="text-[13px] text-neutral-500">
                {t("support.hoursLine1")}
                <br />
                {t("support.hoursLine2")}
              </div>
            </div>
            <div className="mt-4 text-[13px] text-violet-200">
              {t("support.hoursFooter")}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 lele-card p-8 md:p-12 relative overflow-hidden text-center"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.08] via-indigo-500/[0.04] to-transparent pointer-events-none" />
          <div className="relative">
            <div className="mx-auto mb-4 inline-flex items-center gap-2">
              <Bank size={18} weight="duotone" className="text-violet-300" />
              <span className="font-heading text-xl md:text-2xl">
                {t("closing.brand")}
              </span>
            </div>
            <p className="font-heading text-xl md:text-2xl leading-tight text-neutral-100">
              {t("closing.platform")}
            </p>
            <p className="mt-2 text-neutral-400 text-base">
              {t("closing.line1")}
            </p>
            <p className="mt-1 text-neutral-500 text-sm">
              {t("closing.line2")}
            </p>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
